// ⚠️  TEAMS LINK CAPTURE — READ _docs/TEAMS_LINK_HANDLING.md BEFORE EDITING ⚠️
//
// This listener is installed on `window` in the native capture phase (before React mounts)
// so that link clicks work even though React synthetic onClick silently fails for buttons
// inside scrollable containers in Teams Desktop's sandboxed WebView2 iframe.
//
// LINK_SELECTOR must stay in sync with the data-attributes used in MarkdownRenderer.tsx.
// Elements with data-open-in-app="true" are intentionally skipped — they own their own
// navigation via React onClick (only safe for elements outside the scroll container).
import { isTeamsEmbeddedOnLoad, normalizeOpenableUrl } from './teamsHost';
import { recordRawLinkClick, recordTeamsLinkAttempt } from './teamsLinkDebug';
import { openExternalLink } from './teamsSdk';

const LINK_SELECTOR =
  'a[href], button[data-external-href], button[data-open-external], button[data-external-link]';

function hrefFromElement(element: Element): string | null {
  if (element instanceof HTMLAnchorElement) {
    return element.getAttribute('href')?.trim() ?? null;
  }
  if (element instanceof HTMLButtonElement) {
    return (
      element.getAttribute('data-external-href')
      || element.getAttribute('data-open-external')
      || element.getAttribute('data-external-link')
    )?.trim() ?? null;
  }
  return null;
}

function handlePointerLikeEvent(event: PointerEvent): void {
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = (event.target as Element | null)?.closest(LINK_SELECTOR);
  if (!target) return;

  const rawHref = hrefFromElement(target);

  // Record the raw click BEFORE checking data-open-in-app so the debug panel can observe
  // every link pointer event, including those whose onClick is handled by React directly.
  recordRawLinkClick({
    phase: event.type,
    tag: target.tagName,
    rawHref: rawHref ?? '(missing)',
    className: target.className,
  });

  // Elements with data-open-in-app="true" own their navigation via React onClick — don't intercept.
  if (target instanceof HTMLElement && target.dataset.openInApp === 'true') return;

  if (!rawHref) return;

  const normalized = normalizeOpenableUrl(rawHref);
  if (!normalized) {
    recordTeamsLinkAttempt(rawHref, `capture rejected: not openable (${event.type})`);
    return;
  }

  // Only suppress default navigation for <a> elements — they have a real href that the browser
  // would otherwise follow.  <button> elements have no default navigation behavior, so calling
  // preventDefault() on pointerdown for them would only cancel the text-selection default action,
  // which prevents users from selecting/copying message text in Teams Desktop WebView2.
  if (target instanceof HTMLAnchorElement) {
    event.preventDefault();
  }
  recordTeamsLinkAttempt(normalized, `capture → openExternalLink (${event.type})`);
  openExternalLink(normalized);
}

/**
 * Inject a <style> block that re-enables text selection inside the messages area.
 *
 * Teams Desktop (WebView2) injects host-level CSS with user-select:none before our
 * bundle loads.  Our compiled stylesheet fights it with !important (see teams.css),
 * but appending a <style> tag at runtime is appended AFTER all earlier stylesheets in
 * the cascade — giving it the last-author-wins advantage even without !important.
 * Both layers together provide the most reliable override.
 */
function injectTeamsTextSelectionFix(): void {
  if (document.getElementById('cw-teams-select-fix')) return;
  const style = document.createElement('style');
  style.id = 'cw-teams-select-fix';
  style.textContent = [
    '.messages-scroll-container,',
    '.messages-scroll-container *,',
    '.messages-scroll-light,',
    '.messages-scroll-light * {',
    '  -webkit-user-select: text !important;',
    '  user-select: text !important;',
    '}',
  ].join('\n');
  (document.head ?? document.documentElement).appendChild(style);
}

/** Install before React mounts so link clicks are never missed in Teams. */
export function installEarlyTeamsLinkCapture(): void {
  if (!isTeamsEmbeddedOnLoad()) return;
  injectTeamsTextSelectionFix();
  window.addEventListener('pointerdown', handlePointerLikeEvent, true);
}
